import { UseQueryResult } from 'react-query';
import { BaseSchema } from 'yup';

import { ManagedIntegrationListQueryResult } from '@frontend/api';

import { FeatureFlag, featureFlagsStaticData } from './const/featureFlags';

export enum FeatureFlagCategory {
  Entitlement = 'Entitlement',
  Configuration = 'Configuration',
  Development = 'Development',
  Backburner = 'Backburner',
}

export enum FeatureFlagAvailability {
  CheckEntitlement = 'CheckEntitlement',
  ConsultRD = 'ConsultR&D',
  CheckInstructions = 'CheckInstructions',
  NotAvailable = 'NotAvailable',
}

export enum FeatureFlagType {
  Boolean = 'boolean',
  Number = 'number',
  String = 'string',
}

// Details about feature flags that help display and categorize them.
// Intended to be maintained by developers shipping features.
export type FeatureFlagData = {
  // Display information
  category: FeatureFlagCategory;
  deprecate?: string;
  explanation: string;
  displayName: string;

  // To be updated throughout development.
  availability: FeatureFlagAvailability;
  contracted: boolean;

  // Learn more link
  learnMoreLink?: `${'https' | 'http'}://${string}.${string}`;

  // Data validation
  nullable?: boolean;
  getSchema?: (
    featureFlags: FeatureFlagFormDataQueryResult,
    managedIntegrations: ManagedIntegrationListQueryResult,
  ) => BaseSchema;
} & (
  | {
      type: FeatureFlagType.Boolean;
      default?: boolean;
      tierInfo: {
        growth: { default?: boolean; upsell: boolean };
        pro: { default?: boolean; upsell: boolean };
        ent: { default?: boolean; upsell: boolean };
        entMs: { default?: boolean; upsell: boolean };
      };
    }
  | {
      type: FeatureFlagType.Number;
      default?: number;
      min: number;
      tierInfo: {
        growth: { default?: number; upsell: boolean };
        pro: { default?: number; upsell: boolean };
        ent: { default?: number; upsell: boolean };
        entMs: { default?: number; upsell: boolean };
      };
    }
  | {
      type: FeatureFlagType.String;
      default?: string;
      allowedValues: string[];
      tierInfo: {
        growth: { default?: string; upsell: boolean };
        pro: { default?: string; upsell: boolean };
        ent: { default?: string; upsell: boolean };
        entMs: { default?: string; upsell: boolean };
      };
    }
);

// Utility types to map flag key to its value type
type FlagTypeToValue = {
  [FeatureFlagType.Boolean]: boolean;
  [FeatureFlagType.Number]: number;
  [FeatureFlagType.String]: string;
};
type FlagType<K extends FeatureFlag> =
  (typeof featureFlagsStaticData)[K]['type'];
type FlagValue<K extends FeatureFlag> = FlagTypeToValue[FlagType<K>];

// Form data type - maps each flag key to its value (nullable if flag allows it)
export type FeatureFlagFormData = {
  [K in FeatureFlag]: (typeof featureFlagsStaticData)[K]['nullable'] extends true
    ? FlagValue<K> | null
    : FlagValue<K>;
};

// Query result type for Formik
export type FeatureFlagFormDataQueryResult =
  UseQueryResult<FeatureFlagFormData>['data'];
